import { useCallback, useRef } from 'react'

/**
 * How far beyond the viewport the list renders, and therefore how close to the last row counts as "at the
 * bottom" — for the gate below AND for Virtuoso's own `atBottomStateChange`.
 *
 * ONE constant for all three, deliberately. The gate asks for a page when the operator reaches this zone and
 * re-arms when they leave it, so a Virtuoso prop tuned on its own would leave the two measuring different
 * boundaries: ask at 400px, re-arm at 4px, and a twitch of the wheel near the bottom becomes another page.
 */
export const VIRTUOSO_OVERSCAN_PX = 400

interface ScrollGatedPagination {
    /** Wire to Virtuoso's `endReached`. */
    endReached: () => void
    /** Wire to Virtuoso's `isScrolling` — this is what answers an `endReached` that came too early. */
    isScrolling: (scrolling: boolean) => void
    /** Wire to Virtuoso's `atBottomStateChange` — leaving the bottom is what earns the next page. */
    atBottomStateChange: (atBottom: boolean) => void
    /** Wire to Virtuoso's `scrollerRef`. Stable, so its scroller effect does not re-run every render. */
    captureScroller: (ref: HTMLElement | Window | null) => void
}

/** Why a request was or was not made — the outcomes are answered differently, see `endReached`. */
type AskOutcome = 'asked' | 'already-asked-down-here' | 'not-at-the-end'

/**
 * Infinite scroll: ONE page per trip to the bottom, and only on the operator's own scroll.
 *
 * Two things about Virtuoso make that harder than it sounds, and both were live bugs on this board:
 *
 * 1. `endReached` fires when the last row enters the RENDERED window, which is `increaseViewportBy.bottom`
 *    (400px) taller than the viewport. On a board whose client refinements (Age, min/max miles, the text
 *    filter) hide most of a fetched page, the last row is on screen from the first render with nothing
 *    scrolled at all. `scrollTop` is the part no overscan can fake: it is 0 until the operator scrolls.
 *
 * 2. The emission is deduped on `[lastIndex, data]` — the DATA ARRAY IDENTITY, not its length. Every page
 *    that lands, and every live load the socket prepends, hands the list a new array. So parking at the
 *    bottom re-emits `endReached` on each one, and the board pages through the whole feed on its own while
 *    the operator sits still.
 *
 * Position alone therefore cannot decide, and neither can the row count (a live load changes it without the
 * operator moving). What decides is the VISIT: reaching the bottom zone buys exactly one page, and the next
 * one has to be earned by leaving the zone and coming back — which is the operator scrolling, the only thing
 * that can mean "show me more".
 *
 * @param onEndReached fetch the next page; omitted → the list does not paginate.
 */
export const useScrollGatedPagination = (onEndReached: (() => void) | undefined): ScrollGatedPagination => {
    // The list's own scroller, so the gate can ask where the operator actually is. An `endReached` that came
    // too early waits in `endPendingRef` for the scroll that earns it.
    const scrollerRef = useRef<HTMLElement | null>(null)
    const endPendingRef = useRef(false)
    /** A page was already asked for during this stay in the bottom zone. */
    const askedDownHereRef = useRef(false)

    const requestNextPage = useCallback((): AskOutcome => {
        const el = scrollerRef.current
        if (!el || el.scrollTop <= 0) return 'not-at-the-end'
        if (el.scrollHeight - el.scrollTop - el.clientHeight > VIRTUOSO_OVERSCAN_PX) return 'not-at-the-end'
        if (askedDownHereRef.current) return 'already-asked-down-here'
        askedDownHereRef.current = true
        onEndReached?.()
        return 'asked'
    }, [onEndReached])

    /**
     * Refused for POSITION, Virtuoso does not come back: it emits once per (last index, data array), so a
     * list that was too short to scroll and then became scrollable — the filter panel collapsed, a column
     * hidden, the window resized — would never ask again. That refusal is remembered and answered on the
     * next scroll. A refusal because this trip to the bottom already bought its page is NOT remembered:
     * nothing is pending, the answer stands until the operator leaves and returns.
     */
    const endReached = useCallback(() => {
        if (requestNextPage() === 'not-at-the-end') endPendingRef.current = true
    }, [requestNextPage])

    const isScrolling = useCallback(
        (scrolling: boolean) => {
            if (scrolling || !endPendingRef.current) return
            if (requestNextPage() !== 'not-at-the-end') endPendingRef.current = false
        },
        [requestNextPage]
    )

    /**
     * Leaving the bottom zone re-arms the gate; arriving does not ask by itself (`endReached` does that).
     *
     * Virtuoso reports this against `atBottomThreshold`, which the table sets to the same 400px the gate
     * measures — so "left" here means the same thing as "not at the end" there.
     */
    const atBottomStateChange = useCallback((atBottom: boolean) => {
        if (!atBottom) askedDownHereRef.current = false
    }, [])

    const captureScroller = useCallback((ref: HTMLElement | Window | null) => {
        scrollerRef.current = ref instanceof HTMLElement ? ref : null
    }, [])

    return { endReached, isScrolling, atBottomStateChange, captureScroller }
}
